import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import type { Json, Tables, TablesInsert, TablesUpdate } from "~/api/tables";
import {
  depreciationEstimate,
  nzIncomeYear,
  nzKilometreRate,
  parseQuoteItems,
  type VehicleType,
} from "~/utils/nz-finance";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { db } from "~/api/db";
import {
  documentReferenceFields,
  requireText,
  rootKey,
  workspaceKey,
  textOrNull,
  todayIsoDate,
  useWorkspaceMutation,
  type AssetDepreciationEntry,
  type FixedAsset,
  type MileageEntry,
  type Quote,
  type QuoteWithItems,
} from "./shared";

export function useWorkspaceQuotes(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("quotes", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as QuoteWithItems[];
      const { data, error } = await db
        .from("quotes")
        .select("*, quote_items(*)")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as QuoteWithItems[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "quotes",
    async (input: Record<string, string>, targetWorkspaceId) => {
      const items = parseQuoteItems(requireText(input.lineItems, "Line items"));
      return unwrap(
        api.createQuote({
          body: {
            workspace_id: targetWorkspaceId,
            client_id: textOrNull(input.clientId),
            project_id: textOrNull(input.projectId),
            title: requireText(input.title, "Quote title"),
            valid_until: textOrNull(input.validUntil),
            notes: textOrNull(input.notes),
            terms: textOrNull(input.terms),
            items,
            ...documentReferenceFields(input),
          },
        }),
      );
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "quotes",
    async (input: { id: string; values: Record<string, string> }) => {
      const items = parseQuoteItems(
        requireText(input.values.lineItems, "Line items"),
      );
      return unwrap(
        api.updateQuote({
          path: { id: input.id },
          body: {
            client_id: textOrNull(input.values.clientId),
            project_id: textOrNull(input.values.projectId),
            title: requireText(input.values.title, "Quote title"),
            valid_until: textOrNull(input.values.validUntil),
            notes: textOrNull(input.values.notes),
            terms: textOrNull(input.values.terms),
            items,
            ...documentReferenceFields(input.values),
          },
        }),
      );
    },
  );

  const updateAttachment = useMutation({
    mutationFn: async (input: {
      id: string;
      attachmentPath: string | null;
      attachmentFilename: string | null;
      attachmentMimeType: string | null;
      attachmentSizeBytes: number | null;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("quotes")
        .update({
          attachment_path: input.attachmentPath,
          attachment_filename: input.attachmentFilename,
          attachment_mime_type: input.attachmentMimeType,
          attachment_size_bytes: input.attachmentSizeBytes,
        } satisfies TablesUpdate<"quotes">)
        .eq("id", input.id)
        .eq("workspace_id", workspaceId);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: workspaceKey("quotes", workspaceId),
      }),
  });

  const setStatus = useMutation({
    mutationFn: async (input: { id: string; status: Quote["status"] }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const now = new Date().toISOString();
      const patch: TablesUpdate<"quotes"> = { status: input.status };
      if (input.status === "sent") patch.sent_at = now;
      if (input.status === "accepted") patch.accepted_at = now;
      if (input.status === "declined") patch.declined_at = now;
      const { error } = await db
        .from("quotes")
        .update(patch)
        .eq("workspace_id", workspaceId)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: workspaceKey("quotes", workspaceId),
      }),
  });

  const sendEmail = useMutation({
    mutationFn: async (quoteId: string) => {
      return unwrap(api.sendQuoteEmail({ path: { id: quoteId } }));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: workspaceKey("quotes", workspaceId),
      }),
  });

  const scheduleSend = useMutation({
    mutationFn: async (input: { id: string; date: string; time: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const date = requireText(input.date, "Send date");
      const time = requireText(input.time, "Send time");
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error("Send date must be YYYY-MM-DD.");
      }
      if (!/^\d{2}:\d{2}$/.test(time)) {
        throw new Error("Send time must be HH:mm.");
      }
      const { error } = await db
        .from("quotes")
        .update({
          scheduled_send_at: `${date}T${time}:00+12:00`,
          scheduled_send_status: "scheduled",
          scheduled_send_error: null,
        } satisfies TablesUpdate<"quotes">)
        .eq("id", input.id)
        .eq("workspace_id", workspaceId)
        .eq("status", "draft");
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: workspaceKey("quotes", workspaceId),
      }),
  });

  const cancelScheduledSend = useMutation({
    mutationFn: async (quoteId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("quotes")
        .update({
          scheduled_send_at: null,
          scheduled_send_status: "cancelled",
          scheduled_send_error: null,
        } satisfies TablesUpdate<"quotes">)
        .eq("id", quoteId)
        .eq("workspace_id", workspaceId)
        .eq("scheduled_send_status", "scheduled");
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: workspaceKey("quotes", workspaceId),
      }),
  });

  const convertToInvoice = useMutation({
    mutationFn: async (input: { id: string; dueDate: string }) => {
      return unwrap(
        api.convertQuoteToInvoice({
          path: { id: input.id },
          body: { due_date: requireText(input.dueDate, "Invoice due date") },
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceKey("quotes", workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  const remove = useWorkspaceMutation(
    workspaceId,
    "quotes",
    async (quoteId: string, targetWorkspaceId) => {
      const { error } = await db
        .from("quotes")
        .delete()
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", quoteId)
        .eq("status", "draft");
      if (error) throw error;
    },
  );

  return {
    ...query,
    cancelScheduledSend,
    quotes: query.data ?? [],
    create,
    convertToInvoice,
    remove,
    scheduleSend,
    sendEmail,
    setStatus,
    update,
    updateAttachment,
  };
}

export function useWorkspaceExpenses(workspaceId: string | undefined) {
  const query = useQuery({
    queryKey: workspaceKey("expenses", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as Tables<"expenses">[];
      const { data, error } = await db
        .from("expenses")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("expense_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Tables<"expenses">[];
    },
    enabled: Boolean(workspaceId),
  });
  return { ...query, expenses: query.data ?? [] };
}

function mileageRows(
  input: Record<string, string>,
  ytdKmBefore: number,
): Array<{
  travelled_on: string;
  vehicle_name: string;
  registration: string | null;
  vehicle_type: VehicleType;
  origin: string | null;
  destination: string | null;
  purpose: string;
  odometer_start_km: number | null;
  odometer_end_km: number | null;
  business_km: number;
  income_year: number;
  rate_tier: 1 | 2;
  rate_cents_per_km: number;
  notes: string | null;
}> {
  const vehicleType = (
    ["petrol", "diesel", "petrol_hybrid", "electric", "other"].includes(
      input.vehicleType ?? "",
    )
      ? input.vehicleType
      : "petrol"
  ) as VehicleType;
  const businessKm = Number(input.businessKm);
  if (!Number.isFinite(businessKm) || businessKm <= 0) {
    throw new Error("Business kilometres must be greater than zero.");
  }
  const derivedTier: 1 | 2 = ytdKmBefore >= 14000 ? 2 : 1;
  const requestedTier =
    input.rateTier === "2" ? 2 : input.rateTier === "1" ? 1 : derivedTier;
  if (requestedTier === 1 && ytdKmBefore >= 14000) {
    throw new Error(
      "This vehicle already has 14,000+ business km this income year. Use Tier 2.",
    );
  }

  const travelledOn = textOrNull(input.travelledOn) ?? todayIsoDate();
  const odometerStart = input.odometerStartKm?.trim()
    ? Number(input.odometerStartKm)
    : null;
  const odometerEnd = input.odometerEndKm?.trim()
    ? Number(input.odometerEndKm)
    : null;
  const base = {
    travelled_on: travelledOn,
    vehicle_name: requireText(input.vehicleName, "Vehicle"),
    registration: textOrNull(input.registration),
    vehicle_type: vehicleType,
    origin: textOrNull(input.origin),
    destination: textOrNull(input.destination),
    purpose: requireText(input.purpose, "Business purpose"),
    odometer_start_km:
      odometerStart !== null && Number.isFinite(odometerStart)
        ? odometerStart
        : null,
    odometer_end_km:
      odometerEnd !== null && Number.isFinite(odometerEnd) ? odometerEnd : null,
    income_year: nzIncomeYear(travelledOn),
    notes: textOrNull(input.notes),
  };

  const enteredRate = Number(input.rateCents);
  const rateFor = (tier: 1 | 2) => {
    if (Number.isFinite(enteredRate) && enteredRate > 0) return enteredRate;
    return nzKilometreRate(vehicleType, tier);
  };

  // Crossing the IRD Tier 1 cap: split into Tier 1 remainder + Tier 2 remainder
  // when the user is still on Tier 1 (explicit or derived). Explicit Tier 2 stays
  // a single row at the higher rate.
  const tier1Remaining = Math.max(0, 14000 - ytdKmBefore);
  if (
    requestedTier === 1 &&
    tier1Remaining > 0 &&
    businessKm > tier1Remaining
  ) {
    const tier1Km = tier1Remaining;
    const tier2Km = businessKm - tier1Km;
    const tier1Rate = rateFor(1);
    const tier2Rate = rateFor(2);
    if (tier1Rate <= 0 || tier2Rate <= 0) {
      throw new Error("Enter the rate for this vehicle.");
    }
    return [
      {
        ...base,
        business_km: tier1Km,
        rate_tier: 1,
        rate_cents_per_km: tier1Rate,
      },
      {
        ...base,
        business_km: tier2Km,
        rate_tier: 2,
        rate_cents_per_km: tier2Rate,
        notes: base.notes
          ? `${base.notes} (auto-split past 14,000 km)`
          : "Auto-split past 14,000 km Tier 1 limit",
      },
    ];
  }

  const tier = requestedTier;
  const rate = rateFor(tier);
  if (rate <= 0) throw new Error("Enter the rate for this vehicle.");
  return [
    {
      ...base,
      business_km: businessKm,
      rate_tier: tier,
      rate_cents_per_km: rate,
    },
  ];
}

function ytdBusinessKm(input: {
  entries: MileageEntry[];
  vehicleName: string;
  registration: string | null;
  incomeYear: number;
  excludeId?: string;
}) {
  const vehicleName = input.vehicleName.trim().toLowerCase();
  const registration = (input.registration ?? "").trim().toLowerCase();
  return input.entries
    .filter((entry) => {
      if (input.excludeId && entry.id === input.excludeId) return false;
      if (entry.income_year !== input.incomeYear) return false;
      if (entry.vehicle_name.trim().toLowerCase() !== vehicleName) return false;
      return (entry.registration ?? "").trim().toLowerCase() === registration;
    })
    .reduce((total, entry) => total + Number(entry.business_km), 0);
}

export function useWorkspaceMileage(workspaceId: string | undefined) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: workspaceKey("mileage", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as MileageEntry[];
      const { data, error } = await db
        .from("mileage_entries")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("travelled_on", { ascending: false });
      if (error) throw error;
      return (data ?? []) as MileageEntry[];
    },
    enabled: Boolean(workspaceId),
  });
  const create = useWorkspaceMutation(
    workspaceId,
    "mileage",
    async (input: Record<string, string>, targetWorkspaceId) => {
      if (!user) throw new Error("Sign in to record mileage.");
      const travelledOn = textOrNull(input.travelledOn) ?? todayIsoDate();
      const vehicleName = requireText(input.vehicleName, "Vehicle");
      const registration = textOrNull(input.registration);
      const ytdKm = ytdBusinessKm({
        entries: query.data ?? [],
        vehicleName,
        registration,
        incomeYear: nzIncomeYear(travelledOn),
      });
      const rows = mileageRows(input, ytdKm);
      const { error } = await db.from("mileage_entries").insert(
        rows.map(
          (row) =>
            ({
              ...row,
              workspace_id: targetWorkspaceId,
              created_by: user.id,
            }) satisfies TablesInsert<"mileage_entries">,
        ),
      );
      if (error) throw error;
    },
  );
  const update = useWorkspaceMutation(
    workspaceId,
    "mileage",
    async (
      input: { id: string; values: Record<string, string> },
      targetWorkspaceId,
    ) => {
      const travelledOn =
        textOrNull(input.values.travelledOn) ?? todayIsoDate();
      const vehicleName = requireText(input.values.vehicleName, "Vehicle");
      const registration = textOrNull(input.values.registration);
      const ytdKm = ytdBusinessKm({
        entries: query.data ?? [],
        vehicleName,
        registration,
        incomeYear: nzIncomeYear(travelledOn),
        excludeId: input.id,
      });
      const rows = mileageRows(input.values, ytdKm);
      if (rows.length !== 1) {
        throw new Error(
          "This trip crosses the 14,000 km Tier 1 limit. Delete and re-add it so MintStint can split the trip, or set Tier 2 for the whole trip.",
        );
      }
      const { error } = await db
        .from("mileage_entries")
        .update(rows[0]! satisfies TablesUpdate<"mileage_entries">)
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", input.id);
      if (error) throw error;
    },
  );
  const remove = useWorkspaceMutation(
    workspaceId,
    "mileage",
    async (id: string, targetWorkspaceId) => {
      const { error } = await db
        .from("mileage_entries")
        .delete()
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", id);
      if (error) throw error;
    },
  );
  return { ...query, entries: query.data ?? [], create, update, remove };
}

function moneyToCents(value: string | undefined, label: string) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0)
    throw new Error(`${label} is invalid.`);
  return Math.round(amount * 100);
}

function fixedAssetValues(input: Record<string, string>) {
  const originalCost = moneyToCents(input.originalCost, "Original cost");
  if (originalCost <= 0)
    throw new Error("Original cost must be greater than zero.");
  const gst = moneyToCents(input.gst, "GST");
  const depreciableCost = input.depreciableCost?.trim()
    ? moneyToCents(input.depreciableCost, "Depreciable cost")
    : originalCost - gst;
  const rate = Number(input.depreciationRate);
  const businessUse = Number(input.businessUsePercent || "100");
  const method =
    input.depreciationMethod === "straight_line" ||
    input.depreciationMethod === "none"
      ? input.depreciationMethod
      : "diminishing_value";
  if (gst > originalCost) throw new Error("GST cannot exceed original cost.");
  if (depreciableCost <= 0) {
    throw new Error("Depreciable cost must be greater than zero.");
  }
  if (!Number.isFinite(businessUse) || businessUse < 0 || businessUse > 100) {
    throw new Error("Business use must be between 0 and 100%.");
  }
  if (
    method !== "none" &&
    (!Number.isFinite(rate) || rate <= 0 || rate > 100)
  ) {
    throw new Error("Enter the IRD depreciation rate between 0 and 100%.");
  }
  return {
    asset_number: textOrNull(input.assetNumber),
    name: requireText(input.name, "Asset name"),
    category: requireText(input.category, "Category"),
    description: textOrNull(input.description),
    purchase_date: requireText(input.purchaseDate, "Purchase date"),
    in_service_date: requireText(input.inServiceDate, "In-service date"),
    original_cost_cents: originalCost,
    gst_cents: gst,
    depreciable_cost_cents: depreciableCost,
    depreciation_method: method,
    depreciation_rate: method === "none" ? 0 : rate,
    business_use_percent: businessUse,
    investment_boost: input.investmentBoost === "true",
    disposal_date: textOrNull(input.disposalDate),
    disposal_proceeds_cents: input.disposalProceeds?.trim()
      ? moneyToCents(input.disposalProceeds, "Disposal proceeds")
      : null,
    notes: textOrNull(input.notes),
  } satisfies TablesUpdate<"fixed_assets">;
}

export function useWorkspaceAssets(workspaceId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("assets", workspaceId),
    queryFn: async () => {
      if (!workspaceId)
        return {
          assets: [] as FixedAsset[],
          entries: [] as AssetDepreciationEntry[],
        };
      const { data: assets, error } = await db
        .from("fixed_assets")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("purchase_date", { ascending: false });
      if (error) throw error;
      const assetRows = (assets ?? []) as FixedAsset[];
      if (assetRows.length === 0)
        return { assets: assetRows, entries: [] as AssetDepreciationEntry[] };
      const { data: entries, error: entriesError } = await db
        .from("asset_depreciation_entries")
        .select("*")
        .in(
          "asset_id",
          assetRows.map((asset) => asset.id),
        )
        .order("income_year", { ascending: false });
      if (entriesError) throw entriesError;
      return {
        assets: assetRows,
        entries: (entries ?? []) as AssetDepreciationEntry[],
      };
    },
    enabled: Boolean(workspaceId),
  });
  const create = useWorkspaceMutation(
    workspaceId,
    "assets",
    async (input: Record<string, string>, targetWorkspaceId) => {
      if (!user) throw new Error("Sign in to add an asset.");
      const { error } = await db.from("fixed_assets").insert({
        ...fixedAssetValues(input),
        workspace_id: targetWorkspaceId,
        created_by: user.id,
      } as TablesInsert<"fixed_assets">);
      if (error) throw error;
    },
  );
  const update = useWorkspaceMutation(
    workspaceId,
    "assets",
    async (
      input: { id: string; values: Record<string, string> },
      targetWorkspaceId,
    ) => {
      const { error } = await db
        .from("fixed_assets")
        .update(fixedAssetValues(input.values))
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", input.id);
      if (error) throw error;
    },
  );
  const saveEstimate = useMutation({
    mutationFn: async (input: { asset: FixedAsset; incomeYear: number }) => {
      const schedule = (query.data?.entries ?? [])
        .filter((entry) => entry.asset_id === input.asset.id)
        .sort((a, b) => a.income_year - b.income_year);
      const firstIncomeYear = nzIncomeYear(input.asset.in_service_date);
      for (let year = firstIncomeYear; year <= input.incomeYear; year += 1) {
        if (
          schedule.some((entry) => entry.income_year === year) &&
          year !== input.incomeYear
        )
          continue;
        const estimate = depreciationEstimate(input.asset, schedule, year);
        if (estimate.monthsUsed === 0) continue;
        const row = {
          asset_id: input.asset.id,
          income_year: estimate.incomeYear,
          months_used: estimate.monthsUsed,
          opening_adjusted_value_cents: estimate.openingAdjustedValueCents,
          investment_boost_cents: estimate.investmentBoostCents,
          investment_boost_claim_cents: estimate.investmentBoostClaimCents,
          depreciation_cents: estimate.depreciationCents,
          depreciation_claim_cents: estimate.depreciationClaimCents,
          closing_adjusted_value_cents: estimate.closingAdjustedValueCents,
          is_estimate: true,
        } satisfies TablesInsert<"asset_depreciation_entries">;
        const { error } = await db
          .from("asset_depreciation_entries")
          .upsert(row, { onConflict: "asset_id,income_year" });
        if (error) throw error;
        const existingIndex = schedule.findIndex(
          (entry) => entry.income_year === year,
        );
        const scheduleRow = {
          ...row,
          id: existingIndex >= 0 ? schedule[existingIndex]!.id : "estimate",
          created_at: "",
          updated_at: "",
        } as AssetDepreciationEntry;
        if (existingIndex >= 0) schedule[existingIndex] = scheduleRow;
        else schedule.push(scheduleRow);
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: workspaceKey("assets", workspaceId),
      }),
  });
  const remove = useWorkspaceMutation(
    workspaceId,
    "assets",
    async (id: string, targetWorkspaceId) => {
      const { error } = await db
        .from("fixed_assets")
        .delete()
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", id);
      if (error) throw error;
    },
  );
  return {
    ...query,
    assets: query.data?.assets ?? [],
    depreciationEntries: query.data?.entries ?? [],
    create,
    update,
    saveEstimate,
    remove,
  };
}
